import { useState } from 'react';
import toast from 'react-hot-toast';
import {
  useCommentAutoReplyRules,
  useSaveCommentAutoReplyRule,
  useDeleteCommentAutoReplyRule,
  useFacebookComments,
  useInstagramComments,
} from '@/api/useSocialComments';
import { useTiktokComments } from '@/api/useTiktok';
import { CommentFeed } from '@/components/social-comments/CommentFeed';
import { AutoReplyRuleList } from '@/components/social-comments/AutoReplyRuleList';
import { AutoReplyRuleEditor } from '@/components/social-comments/AutoReplyRuleEditor';
import type { CommentAutoReplyRule, CommentPlatform, SocialComment } from '@/types/outreach';
import styles from './SocialComments.module.css';

type PlatformFilter = 'ALL' | CommentPlatform;

const PLATFORM_TABS: { value: PlatformFilter; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'FACEBOOK', label: 'Facebook' },
  { value: 'INSTAGRAM', label: 'Instagram' },
  { value: 'TIKTOK', label: 'TikTok' },
];

export function SocialComments() {
  const [platformFilter, setPlatformFilter] = useState<PlatformFilter>('ALL');
  const [commentSearch, setCommentSearch] = useState('');
  const [editingRule, setEditingRule] = useState<CommentAutoReplyRule | null | undefined>(
    undefined,
  );
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // API hooks
  const { data: rulesData, isLoading: rulesLoading } = useCommentAutoReplyRules(
    platformFilter !== 'ALL' ? platformFilter : undefined,
  );
  const saveRule = useSaveCommentAutoReplyRule();
  const deleteRule = useDeleteCommentAutoReplyRule();

  const { data: fbData, isLoading: fbLoading } = useFacebookComments(commentSearch || undefined);
  const { data: igData, isLoading: igLoading } = useInstagramComments(commentSearch || undefined);
  const { data: ttData, isLoading: ttLoading } = useTiktokComments(
    undefined,
    commentSearch || undefined,
  );

  // Merge + filter comments
  const allComments: SocialComment[] = [
    ...(fbData?.results ?? []),
    ...(igData?.results ?? []),
    ...(ttData?.results ?? []),
  ].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  const filteredComments =
    platformFilter === 'ALL'
      ? allComments
      : allComments.filter((c) => c.platform === platformFilter);

  const isLoading = fbLoading || igLoading || ttLoading;

  const handleSaveRule = (
    rule: Partial<CommentAutoReplyRule> & { platform: CommentPlatform; name: string },
  ) => {
    saveRule.mutate(rule, {
      onSuccess: () => {
        toast.success(rule.id ? 'Rule updated' : 'Rule created');
        setEditingRule(undefined);
      },
      onError: () => toast.error('Failed to save rule'),
    });
  };

  const handleDeleteRule = (id: string) => {
    setDeletingId(id);
    deleteRule.mutate(id, {
      onSuccess: () => {
        toast.success('Rule deleted');
        setDeletingId(null);
      },
      onError: () => {
        toast.error('Failed to delete rule');
        setDeletingId(null);
      },
    });
  };

  const rules = rulesData?.results ?? [];
  const showEditor = editingRule !== undefined;

  return (
    <div className={styles.container}>
      {/* Top bar */}
      <div className={styles.topBar}>
        <h1 className={styles.title}>Social Comments</h1>
        <div className={styles.platformTabs}>
          {PLATFORM_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setPlatformFilter(tab.value)}
              className={`${styles.tabBtn} ${platformFilter === tab.value ? styles.tabBtnActive : ''} ${tab.value !== 'ALL' ? styles[`tab${tab.value}`] : ''}`}
            >
              {tab.label}
              {tab.value !== 'ALL' && (
                <span className={styles.tabCount}>
                  {tab.value === 'FACEBOOK'
                    ? (fbData?.count ?? 0)
                    : tab.value === 'INSTAGRAM'
                      ? (igData?.count ?? 0)
                      : (ttData?.count ?? 0)}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Main layout */}
      <div className={styles.layout}>
        {/* Left: Comment Feed (60%) */}
        <div className={styles.feedPanel}>
          <CommentFeed
            comments={filteredComments}
            isLoading={isLoading}
            search={commentSearch}
            onSearchChange={setCommentSearch}
            total={filteredComments.length}
          />
        </div>

        {/* Right: Rules Manager (40%) */}
        <div className={styles.rulesPanel}>
          {showEditor ? (
            <div className={styles.editorWrapper}>
              <AutoReplyRuleEditor
                rule={editingRule}
                onSave={handleSaveRule}
                onCancel={() => setEditingRule(undefined)}
                isSaving={saveRule.isPending}
              />
            </div>
          ) : (
            <AutoReplyRuleList
              rules={rules}
              isLoading={rulesLoading}
              onEdit={(rule) => setEditingRule(rule)}
              onDelete={handleDeleteRule}
              onNew={() => setEditingRule(null)}
              deletingId={deletingId}
            />
          )}
        </div>
      </div>
    </div>
  );
}
